import { observer } from 'mobx-react-lite'
import { Link, useHistory } from 'react-router-dom'
import { Button } from 'semantic-ui-react'
import './ResetPin.css'
import FWOTextInput from '../form/FWOTextInput'
import * as Yup from 'yup'
import { Form, Formik, FormikHelpers } from 'formik'
import ProfileService from '../../services/ProfileService'
import React, { useEffect, useRef } from 'react'
import { UserPin } from '../../models/Pin'
import { PATHS } from '../Routes'
import Protected from '../Protected'
import useErrorHandler from '../../hooks/useErrorHandler'
import FormErrorDisplay from '../common/FormErrorMessages'

const ResetPin = observer(() => {
  const validateSchema = Yup.object({
    pin: Yup.string()
      .required('PIN is required')
      .matches(/^[0-9]+$/, 'PIN must be only digits')
      .min(4, 'PIN must be exactly 4 digits')
      .max(4, 'PIN must be exactly 4 digits'),

    comparePin: Yup.string()
      .when(['pin'], {
        is: (pin) => {
          return (
            pin !== null &&
            pin !== undefined &&
            pin.length === 4 &&
            pin.match(/^[0-9]+$/)
          )
        },
        then: Yup.string()
          .required('Please re-enter PIN')
          .oneOf([Yup.ref('pin'), null], 'PINs must match'),
      })
      .nullable(true),
  })
  const [userErrors, , handleErrors, , clearErrors] = useErrorHandler()
  const { resetPin } = ProfileService
  const mountedRef = useRef(false)
  const history = useHistory()
  const queryParams = new URLSearchParams(location.search)

  function handleFormSubmit(values: UserPin, actions: FormikHelpers<UserPin>) {
    clearErrors()
    resetPin({
      pin: values.pin,
      emailAddress: decodeURIComponent(queryParams.get('emailAddress')),
    })
      .then(() => {
        if (mountedRef.current) {
          history.push(PATHS.Profile + '?reset=true')
        }
      })
      .catch((error) => {
        handleErrors(error)
      })
      .finally(() => {
        if (mountedRef.current) {
          window.scrollTo(0, 0)
          actions.setSubmitting(false)
        }
      })
  }

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      window.scrollTo(0, 0)
    }
    return () => {
      mountedRef.current = false
    }
  }, [])

  return (
    <Protected>
      <nav className="breadcrumbs dnd-s-mob">
        <ol>
          <li>
            <Link className="underline" to={PATHS.Profile}>
              Back to previous page
            </Link>
          </li>
        </ol>
      </nav>
      <div className="content-wrapper">
        <article className="main">
          <p></p>
          <h1>Change PIN</h1>
          <p>
            Please enter a 4 digit number to create your PIN. Make sure you
            write down your PIN and keep it in a safe place.
          </p>
          <FormErrorDisplay
            show={userErrors?.length > 0}
            errorsList={userErrors}
            heading="Please review the following errors"
          />
          <Formik
            validationSchema={validateSchema}
            enableReinitialize
            initialValues={new UserPin()}
            onSubmit={(values, actions) => handleFormSubmit(values, actions)}
          >
            {({ isValid, isSubmitting, dirty }) => (
              <Form className="ui form osp-variant large" autoComplete="off">
                <FWOTextInput
                  type="password"
                  placeholder="Enter PIN"
                  label="Enter PIN"
                  ariaLabel="Enter PIN"
                  name="pin"
                  required={true}
                  aria-label="Enter PIN"
                />
                <FWOTextInput
                  type="password"
                  placeholder="Re-enter PIN"
                  label="Re-enter PIN"
                  ariaLabel="Re-enter PIN"
                  name="comparePin"
                  required={true}
                  aria-label="Re-enter PIN"
                />
                <div className="button-group is-right">
                  <Button
                    disabled={isSubmitting || !dirty || !isValid}
                    loading={isSubmitting}
                    type="submit"
                    content="Submit"
                    className="is-primary"
                  />
                  <Button
                    as={Link}
                    to={PATHS.Profile}
                    type="button"
                    content="Cancel"
                    className="is-secondary"
                  />
                </div>
              </Form>
            )}
          </Formik>

          <aside className="sidebar-first"></aside>
          <aside className="sidebar-second"></aside>
        </article>
      </div>
    </Protected>
  )
})

export default ResetPin
